// @react
import React, { useState, ReactElement, useEffect, useContext } from 'react'
// @typings
import {
  GetUserQuery,
  UserToggleProfilePrivacyMutation,
  UserToggleProfilePrivacyMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @graphql
import { useMutation } from '@apollo/client'
import { USER_TOGGLE_PROFILE_PRIVACY } from 'graphql/mutations/user.mutation'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import Sprintf from 'sprintf-js'
// @commons
import strings from 'lib/constants/strings'
// @context
import { SnackbarContext } from 'modules/common/context/snackbar'
// @components
import Toggle from '@bit/atd.web.toggle'
import Modal from '@bit/atd.web.modal'
import Button from '@bit/atd.web.button'
import Grid from '@bit/atd.web.grid'
// @module components

/**
 * 5.0.1.1 Profile - Personal Information - Visibility
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 * endpoint: user/profile
 * response: UserProfileDTO
 *
 * fields:
 * publicProfile
 * amemberDirectoryListing
 *
 */

const vsprintf = Sprintf.vsprintf

type PropsType = {
  data?: GetUserQuery
  isLoading: boolean
}

/**
 *
 * @param data
 * @param isLoading
 * @constructor
 */
const ProfileVisibility = ({ data, isLoading }: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [publicProfile, setPublicProfile] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)

  const modalDescription = publicProfile
    ? strings.PROFILE.VISIBILITY.MAKE_PRIVATE_WARNING
    : strings.PROFILE.VISIBILITY.MAKE_PUBLIC_WARNING

  /****************************************
   * HOOKS
   *****************************************/

  const snackbar = useContext(SnackbarContext)
  const publicProfileVal = data?.user?.visibility?.publicProfile
  useEffect(() => {
    setPublicProfile(!!publicProfileVal)
  }, [isLoading, publicProfileVal])

  /****************************************
   * GQL HOOKS
   *****************************************/

  const [toggleProfilePrivacy] = useMutation<
    UserToggleProfilePrivacyMutation,
    UserToggleProfilePrivacyMutationVariables
  >(USER_TOGGLE_PROFILE_PRIVACY, {
    update(cache) {
      cache.writeQuery({
        query: GET_USER,
        data: {
          user: {
            ...data?.user,
            visibility: {
              ...data?.user?.visibility,
              publicProfile: !publicProfile,
            },
          },
        },
      })
    },
  })

  const modal = (
    <Modal
      footerNoSeparator
      headerNoSeparator
      open={showModal}
      size="medium"
      title="Private Profile"
      onClose={() => setShowModal(false)}
      actions={
        <Grid container justify="space-around" alignItems="center">
          <Grid item>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="primary"
              size="medium"
              onClick={async () => {
                await handlePublicProfileToggle()
                setShowModal(false)
              }}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      }
    >
      <span>{modalDescription}</span>
    </Modal>
  )

  /****************************************
   * FUNCTIONS
   *****************************************/

  const ctaHandler = () => {
    setShowModal(true)
  }

  /**
   *
   */
  const handlePublicProfileToggle = async () => {
    const { errors: mutationErrors } = await toggleProfilePrivacy({
      variables: {
        input: !publicProfile,
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Privacy',
        ]),
        show: true,
      })
      setPublicProfile(!publicProfile)
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
  }

  /****************************************
   * RENDER
   *****************************************/

  return (
    <>
      <Toggle
        name="publicProfile"
        label={strings.PROFILE.VISIBILITY.MAKE_PUBLIC}
        labelPlacement="right"
        size="medium"
        disabled={isLoading}
        checked={publicProfile}
        onChange={ctaHandler}
        helperText={
          <span>{strings.PROFILE.VISIBILITY.VISIBILITY_AVAILABLE_WARNING}</span>
        }
      />
      {modal}
    </>
  )
}

export default ProfileVisibility
